import clsx from "clsx";
import React from "react";

import ExpansionArrow from "../../../components/atoms/ExpansionArrow";
import AssignedToSection from "../../../components/molecules/ChatModals/TaskModal/Details/AssignedToSection";
import AuthorSection from "../../../components/molecules/ChatModals/TaskModal/Details/AuthorSection";
import CategorySection from "../../../components/molecules/ChatModals/TaskModal/Details/CategorySection";
import ClientSection from "../../../components/molecules/ChatModals/TaskModal/Details/ClientSection";
import CreatedSection from "../../../components/molecules/ChatModals/TaskModal/Details/CreatedSection";
import DueDateSection from "../../../components/molecules/ChatModals/TaskModal/Details/DueDateSection";
import OwnerSection from "../../../components/molecules/ChatModals/TaskModal/Details/OwnerSection";
import PrimaryAssigneeSection from "../../../components/molecules/ChatModals/TaskModal/Details/PrimaryAssigneeSection";
import PrioritySection from "../../../components/molecules/ChatModals/TaskModal/Details/PrioritySection";
import StatusSection from "../../../components/molecules/ChatModals/TaskModal/Details/StatusSection";
import TeamSection from "../../../components/molecules/ChatModals/TaskModal/Details/TeamSection";
import TypeSection from "../../../components/molecules/ChatModals/TaskModal/Details/TypeSection";
import UpdatedAtSection from "../../../components/molecules/ChatModals/TaskModal/Details/UpdatedAtSection";
import WatchersSection from "../../../components/molecules/ChatModals/TaskModal/Details/WatchersSection";

import { useSupabase } from "@SonarCX/providers/SupabaseProvider";
import useMetaStyles from "../../../components/molecules/ChatModals/TaskModal/TaskMetaSection/styles";
import useDetailsStyles from "../../../components/molecules/ChatModals/TaskModal/styles/TaskDetails";
import useAccess, { PERMS } from "../../../hooks/useAccess";
import { useUserAPI } from "../../../providers/UserAPIProvider";
import { chunkArray } from "../../../utilities";
import useStyles from "./styles";

const ColumnDisplay = ({ items }) => {
  const classes = useStyles();

  const columns = React.useMemo(() => {
    const splitPoint = Math.ceil(items.length / 2);
    return chunkArray(items, splitPoint);
  }, [items]);

  return (
    <div className={classes.gridContainer}>
      {columns.map((column, i) =>
        column.map((item, j) => (
          <div
            key={`${i}-${j}`}
            style={{
              gridColumn: `${i + 1} / ${i + 2}`,
              gridRow: `${j + 1} / ${j + 2}`,
            }}
          >
            {item}
          </div>
        ))
      )}
    </div>
  );
};

type CombinedTaskMetaProps = {
  values: any;
  currentUser: any;
  categories: any;
  types: any;
  statuses: any;
  priorities: any;
  handleChange: any;
  searchUser: any;
  sendValues: any;
  setModifiedTask: any;
  canComplete: any;
  canEdit: any;
  setNewUserName: any;
  newUserName: any;
  canSearchUser: any;
  canAddClient: any;
  canUpdate: any;
  teams: Array<APITeam>;
  isSubTask?: boolean;
};

const CombinedTaskMeta: React.FC<CombinedTaskMetaProps> = ({
  values,
  currentUser,
  categories,
  types,
  statuses,
  priorities,
  handleChange,
  searchUser,
  sendValues,
  setModifiedTask,
  canComplete,
  canEdit,
  setNewUserName,
  newUserName,
  canSearchUser,
  canAddClient,
  canUpdate,
  teams,
  isSubTask,
}) => {
  const { getUsers, allUsers, loadUser } = useUserAPI();
  const hasPermission = useAccess();
  const {
    taskCategoryRules: { applyTaskCategoryRules },
  } = useSupabase();

  const [clientObj, setClientObj] = React.useState<string | LocalUser>("");
  const [leadObj, setLeadObj] = React.useState(null);
  const [ownerObj, setOwnerObj] = React.useState(null);
  const [expanded, setExpanded] = React.useState(false);

  const metaClasses = useMetaStyles({ modal: false });
  const detailsClasses = useDetailsStyles();
  const classes = useStyles();

  // handler for removing field on clicking "x"
  const removeUser = React.useCallback(
    (prop, user) => {
      if (["primary", "watchers"].includes(prop)) {
        handleChange(prop)({ target: { value: user.id, action: "clear" } });
      }
    },
    [handleChange]
  );

  // Set client
  React.useEffect(() => {
    const id = values.client;

    if (!id) {
      setClientObj("");
      return;
    }

    if (hasPermission([PERMS.ContactsOwn, PERMS.ContactsAudit])) {
      const guests = getUsers(true);
      const guestName = guests?.[id];
      setClientObj(guestName);
      return;
    }

    const user = allUsers[id];
    if (user) {
      setClientObj(user);
    } else {
      loadUser(id);
    }
  }, [values, getUsers, loadUser, allUsers, hasPermission]);

  // Set lead
  React.useEffect(() => {
    const id = values.primary_assignee_id;

    if (!id) {
      setLeadObj(null);
      return;
    }

    const user = allUsers[id];
    if (user) {
      setLeadObj(user);
    } else {
      loadUser(id);
    }
  }, [values, allUsers, loadUser]);

  // Set onwer
  React.useEffect(() => {
    const id = values.owner_id;

    if (!id) {
      setOwnerObj(null);
      return;
    }

    const user = allUsers[id];
    if (user) {
      setOwnerObj(user);
    } else {
      loadUser(id);
    }
  }, [values, allUsers, loadUser]);

  const topItems = React.useMemo(
    () => [
      <div className={clsx(detailsClasses.gridItem, metaClasses.specialItem)}>
        <ClientSection
          classes={detailsClasses}
          canEdit={isSubTask ? false : canEdit}
          canAddClient={canAddClient}
          clientObj={clientObj}
          sendValues={sendValues}
          newUserName={newUserName}
          setNewUserName={setNewUserName}
          fullPage
        />
      </div>,

      <div className={clsx(detailsClasses.gridItem, metaClasses.specialItem)}>
        <CategorySection
          classes={detailsClasses}
          categories={categories}
          handleChange={handleChange}
          values={values}
          canEdit={canEdit}
        />
      </div>,

      <div className={clsx(detailsClasses.gridItem, metaClasses.specialItem)}>
        <TypeSection
          classes={detailsClasses}
          types={applyTaskCategoryRules(values.section, types)}
          values={values}
          canEdit={canEdit}
          handleChange={handleChange}
        />
      </div>,

      <div className={clsx(detailsClasses.gridItem, metaClasses.specialItem)}>
        <PrioritySection
          classes={detailsClasses}
          priorities={priorities}
          canEdit={canEdit}
          handleChange={handleChange}
          values={values}
        />
      </div>,

      <div className={clsx(detailsClasses.gridItem, metaClasses.specialItem)}>
        <DueDateSection
          classes={detailsClasses}
          handleChange={handleChange}
          canEdit={canEdit}
          values={values}
        />
      </div>,

      <div className={clsx(detailsClasses.gridItem, metaClasses.specialItem)}>
        <OwnerSection
          classes={detailsClasses}
          canEdit={isSubTask ? false : canEdit}
          setModifiedTask={setModifiedTask}
          sendValues={sendValues}
          ownerObj={ownerObj}
          setOwnerObj={setOwnerObj}
          fullPage
        />
      </div>,

      <div className={clsx(detailsClasses.gridItem, metaClasses.specialItem)}>
        <PrimaryAssigneeSection
          classes={detailsClasses}
          canEdit={canEdit}
          setModifiedTask={setModifiedTask}
          sendValues={sendValues}
          leadObj={leadObj}
          setLeadObj={setLeadObj}
          fullPage
        />
      </div>,

      <div className={clsx(detailsClasses.gridItem, metaClasses.specialItem)}>
        <AssignedToSection
          classes={detailsClasses}
          values={values}
          allUsers={allUsers}
          removeUser={removeUser}
          canEdit={canEdit}
          canSearchUser={canSearchUser}
          handleChange={handleChange}
        />
      </div>,

      <div className={clsx(detailsClasses.gridItem, metaClasses.specialItem)}>
        <WatchersSection
          classes={detailsClasses}
          values={values}
          removeUser={removeUser}
          canEdit={canEdit}
          canSearchUser={canSearchUser}
          allUsers={allUsers}
          handleChange={handleChange}
        />
      </div>,

      <div className={clsx(detailsClasses.gridItem, metaClasses.specialItem)}>
        <StatusSection
          classes={detailsClasses}
          statuses={statuses}
          values={values}
          handleChange={handleChange}
          canEdit={canEdit || canUpdate}
          canComplete={canComplete}
        />
      </div>,
    ],
    [
      allUsers,
      canAddClient,
      canComplete,
      canEdit,
      canSearchUser,
      categories,
      clientObj,
      leadObj,
      ownerObj,
      handleChange,
      newUserName,
      priorities,
      removeUser,
      sendValues,
      setModifiedTask,
      setNewUserName,
      statuses,
      types,
      values,
      detailsClasses,
      metaClasses.specialItem,
      canUpdate,
      isSubTask,
      applyTaskCategoryRules,
    ]
  );

  const bottomItems = React.useMemo(
    () => [
      <div className={clsx(detailsClasses.gridItem, metaClasses.specialItem)}>
        <TeamSection
          classes={detailsClasses}
          teams={teams}
          values={values}
          canEdit={canEdit}
          handleChange={handleChange}
        />
      </div>,

      <div className={clsx(detailsClasses.gridItem, metaClasses.specialItem)}>
        <AuthorSection
          classes={detailsClasses}
          allUsers={allUsers}
          currentUser={currentUser}
          values={values}
        />
      </div>,

      <div className={clsx(detailsClasses.gridItem, metaClasses.specialItem)}>
        <CreatedSection classes={detailsClasses} values={values} />
      </div>,

      <div className={clsx(detailsClasses.gridItem, metaClasses.specialItem)}>
        <UpdatedAtSection classes={detailsClasses} values={values} />
      </div>,
    ],
    [
      allUsers,
      canEdit,
      currentUser,
      handleChange,
      values,
      detailsClasses,
      metaClasses.specialItem,
      teams,
    ]
  );

  return (
    <div className={clsx(metaClasses.root, classes.root)}>
      <ColumnDisplay items={topItems} />

      <div className={classes.arrow} onClick={() => setExpanded((old) => !old)}>
        <ExpansionArrow expanded={expanded} />{" "}
        {expanded ? "SHOW LESS" : "SHOW MORE"}
      </div>

      {expanded && <ColumnDisplay items={bottomItems} />}
    </div>
  );
};

export default CombinedTaskMeta;
